<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';

const props = defineProps({
  item: { type: Object, required: true },
  anchorEl: { type: Object, default: null },
});

const emit = defineEmits(['update', 'close']);
const { t } = useI18n();
const popoverRef = ref(null);
const fixedStyle = ref(null);

if (props.anchorEl) {
  nextTick(() => {
    const rect = props.anchorEl.getBoundingClientRect();
    fixedStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      zIndex: 10020,
    };
  });
}

onClickOutside(popoverRef, () => emit('close'));

const RECURRENCE_OPTIONS = [
  { key: 'daily', label: () => t('TASKS.RECURRENCE.DAILY') },
  { key: 'weekdays', label: () => t('TASKS.RECURRENCE.WEEKDAYS') },
  { key: 'weekly', label: () => t('TASKS.RECURRENCE.WEEKLY') },
  { key: 'monthly', label: () => t('TASKS.RECURRENCE.MONTHLY') },
  { key: 'custom', label: () => t('TASKS.RECURRENCE.CUSTOM') },
  { key: 'flexible', label: () => t('TASKS.RECURRENCE.FLEXIBLE') },
];

const UNIT_OPTIONS = [
  { key: 'days', label: () => t('TASKS.RECURRENCE.UNITS.DAYS') },
  { key: 'weeks', label: () => t('TASKS.RECURRENCE.UNITS.WEEKS') },
  { key: 'months', label: () => t('TASKS.RECURRENCE.UNITS.MONTHS') },
  { key: 'years', label: () => t('TASKS.RECURRENCE.UNITS.YEARS') },
];

const recurrenceType = ref(props.item.recurrence_type ?? null);
const customInterval = ref(props.item.recurrence_interval ?? 1);
const customUnit = ref(props.item.recurrence_unit ?? 'days');
function initEndsMode() {
  if (props.item.recurrence_count) return 'after';
  if (props.item.recurrence_ends_at) return 'on_date';
  return 'never';
}
const endsMode = ref(initEndsMode());
const endsAt = ref(props.item.recurrence_ends_at ?? null);
const endsCount = ref(props.item.recurrence_count ?? 2);

const showCustomModal = ref(false);

function buildPayload() {
  return {
    recurrence_type: recurrenceType.value,
    recurrence_interval:
      recurrenceType.value === 'custom' ? customInterval.value : null,
    recurrence_unit:
      recurrenceType.value === 'custom' ? customUnit.value : null,
    recurrence_ends_at: endsMode.value === 'on_date' ? endsAt.value : null,
    recurrence_count: endsMode.value === 'after' ? endsCount.value : null,
  };
}

function selectRecurrence(key) {
  if (key === 'custom') {
    showCustomModal.value = true;
    return;
  }
  recurrenceType.value = recurrenceType.value === key ? null : key;
  emit('update', buildPayload());
  emit('close');
}

function clearRecurrence() {
  recurrenceType.value = null;
  customInterval.value = 1;
  customUnit.value = 'days';
  endsMode.value = 'never';
  endsAt.value = null;
  endsCount.value = 2;
  emit('update', buildPayload());
  emit('close');
}

function cancelCustom() {
  showCustomModal.value = false;
}

watch([customInterval, customUnit, endsMode, endsAt, endsCount], () => {
  if (showCustomModal.value) {
    recurrenceType.value = 'custom';
    emit('update', buildPayload());
  }
});
</script>

<template>
  <Teleport to="body" :disabled="!anchorEl">
    <div
      ref="popoverRef"
      :class="anchorEl ? '' : 'absolute right-0 top-10 z-30'"
      :style="anchorEl ? fixedStyle : undefined"
      @click.stop
    >
      <!-- Menu principal -->
      <div
        v-if="!showCustomModal"
        class="w-56 bg-n-background border border-n-weak rounded-xl shadow-xl py-1"
      >
        <!-- Cabeçalho -->
        <div
          class="flex items-center gap-2 pl-6 pr-3 py-2 border-b border-n-weak"
        >
          <span class="i-lucide-repeat size-3.5 text-n-brand flex-shrink-0" />
          <span
            class="flex-1 text-sm text-left"
            :class="recurrenceType ? 'text-n-brand' : 'text-n-slate-11'"
          >
            {{
              recurrenceType
                ? t(`TASKS.RECURRENCE.${recurrenceType.toUpperCase()}`)
                : t('TASKS.RECURRENCE.LABEL')
            }}
          </span>
          <span
            v-if="recurrenceType"
            class="i-lucide-x size-3.5 text-n-slate-9 hover:text-n-ruby-11 cursor-pointer"
            @click.stop="clearRecurrence"
          />
        </div>

        <!-- Opções -->
        <div class="py-1">
          <button
            v-for="opt in RECURRENCE_OPTIONS"
            :key="opt.key"
            type="button"
            class="w-full flex items-center gap-2 pl-6 pr-3 py-2.5 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              recurrenceType === opt.key
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
            "
            @click="selectRecurrence(opt.key)"
          >
            <span
              v-if="recurrenceType === opt.key"
              class="i-lucide-check size-3.5 flex-shrink-0"
            />
            <span v-else class="size-3.5 flex-shrink-0" />
            {{ opt.label() }}
          </button>
        </div>
      </div>

      <!-- Modal personalizado -->
      <div
        v-else
        class="w-72 bg-n-background border border-n-weak rounded-xl shadow-xl overflow-hidden"
        @click.stop
      >
        <!-- Cabeçalho -->
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('TASKS.RECURRENCE.CUSTOM_TITLE') }}
          </span>
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-9 hover:text-n-slate-12 transition-colors"
            @click="cancelCustom"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Repetir a cada -->
        <div class="px-4 py-3 border-b border-n-weak">
          <p class="text-xs text-n-slate-10 mb-2">
            {{ t('TASKS.RECURRENCE.REPEAT_EVERY') }}
          </p>
          <div class="flex items-center gap-2">
            <div
              class="flex items-center border border-n-weak rounded-lg overflow-hidden bg-n-surface-2 w-24"
            >
              <button
                type="button"
                class="px-2.5 py-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none"
                @click="customInterval = Math.max(1, customInterval - 1)"
              >
                −
              </button>
              <span
                class="flex-1 text-center text-sm font-medium text-n-slate-12 tabular-nums"
              >
                {{ customInterval }}
              </span>
              <button
                type="button"
                class="px-2.5 py-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none"
                @click="customInterval++"
              >
                +
              </button>
            </div>
            <div class="flex gap-1 flex-wrap">
              <button
                v-for="u in UNIT_OPTIONS"
                :key="u.key"
                type="button"
                class="px-2.5 py-1.5 text-sm rounded-lg border transition-colors"
                :class="
                  customUnit === u.key
                    ? 'border-n-brand text-n-brand bg-n-alpha-2'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="customUnit = u.key"
              >
                {{ u.label() }}
              </button>
            </div>
          </div>
        </div>

        <!-- Termina em -->
        <div class="px-4 py-3">
          <p class="text-xs text-n-slate-10 mb-2">
            {{ t('TASKS.RECURRENCE.ENDS') }}
          </p>
          <div class="flex flex-col gap-1">
            <!-- Nunca -->
            <button
              type="button"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="
                endsMode === 'never'
                  ? 'bg-n-alpha-2 text-n-brand'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="endsMode = 'never'"
            >
              <span
                class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                :class="
                  endsMode === 'never' ? 'border-n-brand' : 'border-n-weak'
                "
              >
                <span
                  v-if="endsMode === 'never'"
                  class="size-2 rounded-full bg-n-brand"
                />
              </span>
              {{ t('TASKS.RECURRENCE.NEVER') }}
            </button>

            <!-- Em data -->
            <button
              type="button"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="
                endsMode === 'on_date'
                  ? 'bg-n-alpha-2 text-n-brand'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="endsMode = 'on_date'"
            >
              <span
                class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                :class="
                  endsMode === 'on_date' ? 'border-n-brand' : 'border-n-weak'
                "
              >
                <span
                  v-if="endsMode === 'on_date'"
                  class="size-2 rounded-full bg-n-brand"
                />
              </span>
              <span class="flex-shrink-0">{{
                t('TASKS.RECURRENCE.ON_DATE')
              }}</span>
              <input
                v-model="endsAt"
                type="date"
                :disabled="endsMode !== 'on_date'"
                class="ml-auto text-sm bg-n-surface-2 border border-n-weak rounded-lg px-2 py-1 outline-none focus:border-n-brand disabled:opacity-40 text-n-slate-12"
                @click.stop
              />
            </button>

            <!-- Após N ocorrências -->
            <button
              type="button"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="
                endsMode === 'after'
                  ? 'bg-n-alpha-2 text-n-brand'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="endsMode = 'after'"
            >
              <span
                class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                :class="
                  endsMode === 'after' ? 'border-n-brand' : 'border-n-weak'
                "
              >
                <span
                  v-if="endsMode === 'after'"
                  class="size-2 rounded-full bg-n-brand"
                />
              </span>
              <span class="flex-shrink-0">{{
                t('TASKS.RECURRENCE.AFTER')
              }}</span>
              <div
                class="flex items-center border border-n-weak rounded-lg overflow-hidden bg-n-surface-2 ml-auto"
                @click.stop
              >
                <button
                  type="button"
                  :disabled="endsMode !== 'after'"
                  class="px-2 py-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none disabled:opacity-40"
                  @click.stop="endsCount = Math.max(1, endsCount - 1)"
                >
                  −
                </button>
                <span
                  class="w-8 text-center text-sm font-medium text-n-slate-12 tabular-nums"
                >
                  {{ endsCount }}
                </span>
                <button
                  type="button"
                  :disabled="endsMode !== 'after'"
                  class="px-2 py-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none disabled:opacity-40"
                  @click.stop="endsCount++"
                >
                  +
                </button>
              </div>
              <span class="text-n-slate-10 text-xs flex-shrink-0">{{
                t('TASKS.RECURRENCE.OCCURRENCES')
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
